import { useState, useEffect } from 'react';

import Icon from '../../../components/AppIcon';
import QuestionCard from './QuestionCard';
import {
  HIDDEN_BOOKING_PHOTOGRAPHY_TYPES,
  filterVisibleBookingTypes,
  getAllowedPhotographyTypeValuesForCategory,
} from '../../../utils/photographyTypesVisibility';

// COMING_SOON_TYPES: لتفعيل نوع لاحقاً، احذف قيمته من هذا الـ Set
const COMING_SOON_TYPES = new Set(['wedding', 'event']);

const PhotographyTypeStep = ({ formData, onUpdate, isDirectMode, preselectedPhotographer }) => {
  const [selectedType, setSelectedType] = useState(formData?.photographyType || '');

  useEffect(() => {
    const t = formData?.photographyType;
    const allowed = getAllowedPhotographyTypeValuesForCategory(formData?.photographyCategory);

    if (t && HIDDEN_BOOKING_PHOTOGRAPHY_TYPES.has(t)) {
      setSelectedType('');
      onUpdate({ photographyType: '', photographyTypeLabel: '' });
      return;
    }
    if (t && allowed && !allowed.has(t)) {
      setSelectedType('');
      onUpdate({ photographyType: '', photographyTypeLabel: '' });
      return;
    }
    if (t) setSelectedType(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.photographyType, formData?.photographyCategory]);

  // Get photographer's available photography types from specializations
  const photographerSpecializations = preselectedPhotographer?.specializations || 
                                     formData?.directPhotographerProfile?.specializations ||
                                     [];
  
  // Map specializations to photography type values
  // specializations can be either Arabic labels or English values
  const photographerTypes = photographerSpecializations
    .map((spec) => {
      if (['wedding', 'portrait', 'event', 'commercial', 'real-estate', 'food', 'fashion', 'sports', 'product'].includes(spec)) {
        return spec;
      }
      const labelMap = {
        'حفلات الزفاف': 'wedding',
        'التصوير الشخصي': 'portrait',
        'تصوير المناسبات': 'event',
        'التصوير التجاري': 'commercial',
        'تصوير العقارات': 'real-estate',
        'تصوير الطعام': 'food',
        'تصوير الأزياء': 'fashion',
        'التصوير الرياضي': 'sports',
        'تصوير المنتجات': 'product',
      };
      return labelMap[spec] || spec;
    })
    .filter(Boolean)
    .filter((v) => !HIDDEN_BOOKING_PHOTOGRAPHY_TYPES.has(v));

  const photographyTypes = filterVisibleBookingTypes([
    {
      value: 'portrait',
      label: 'التصوير الشخصي',
      description: 'جلسات تصوير شخصية وعائلية',
    },
    {
      value: 'commercial',
      label: 'التصوير التجاري',
      description: 'تصوير المنتجات والإعلانات التجارية',
    },
    {
      value: 'real-estate',
      label: 'تصوير العقارات',
      description: 'تصوير احترافي للعقارات والمباني',
    },
    {
      value: 'food',
      label: 'تصوير الطعام',
      description: 'تصوير احترافي للمطاعم والأطعمة',
    },
    {
      value: 'fashion',
      label: 'تصوير الأزياء',
      description: 'جلسات تصوير الأزياء والموضة',
    },
    {
      value: 'sports',
      label: 'التصوير الرياضي',
      description: 'تصوير الفعاليات والأحداث الرياضية',
    },
    {
      value: 'product',
      label: 'تصوير المنتجات',
      description: 'تصوير احترافي للمنتجات والبضائع',
    },
    {
      value: 'event',
      label: 'تصوير المناسبات',
      description: 'تصوير المؤتمرات والفعاليات والحفلات',
    },
    {
      value: 'wedding',
      label: 'تصوير حفلات الزفاف',
      description: 'تصوير احترافي لحفلات الزفاف والأعراس',
    },
  ]);

  const allowedSet = getAllowedPhotographyTypeValuesForCategory(formData?.photographyCategory);
  const photographyTypesForCategory = allowedSet
    ? photographyTypes.filter((t) => allowedSet.has(t.value))
    : photographyTypes;

  const handleTypeChange = (value, label) => {
    const allowed = getAllowedPhotographyTypeValuesForCategory(formData?.photographyCategory);
    if (allowed && !allowed.has(value)) return;

    // In direct mode, only allow photographer's available types (if specified)
    if (isDirectMode && photographerTypes.length > 0 && !photographerTypes.includes(value)) {
      return;
    }
    
    setSelectedType(value);
    const selectedTypeObj = photographyTypesForCategory.find((t) => t.value === value);
    onUpdate({ 
      photographyType: value,
      photographyTypeLabel: label || selectedTypeObj?.label || value
    });
    
    // Scroll to top after selection to ensure next step (if conditional) is visible
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // فئة التصوير أولاً، ثم تقاطع مع تخصصات المصور في الحجز المباشر
  const availableTypes =
    isDirectMode && photographerTypes.length > 0
      ? photographyTypesForCategory.filter((t) => photographerTypes.includes(t.value))
      : photographyTypesForCategory;

  const getTypeIcon = () => {
    const iconMap = {
      wedding: 'Heart',
      portrait: 'User',
      event: 'Calendar',
      'real-estate': 'Home',
      food: 'UtensilsCrossed',
      product: 'Package',
    };
    return iconMap?.[selectedType] || 'Camera';
  };

  return (
    <QuestionCard
      icon="Camera"
      title="ما نوع التصوير الذي تحتاجه؟"
      description={
        isDirectMode && photographerTypes.length > 0
          ? 'أنواع التصوير المتاحة من المصور المحدد ضمن فئتك'
          : formData?.photographyCategory === 'commercial'
            ? 'اختر نوع التصوير المناسب لنشاطك أو محتواك التسويقي'
            : formData?.photographyCategory === 'individual'
              ? 'اختر نوع الجلسة المناسب لمناسبتك أو لك شخصياً'
              : 'نوع التصوير يؤثر على السعر النهائي والمصورين المتاحين'
      }
    >
      {isDirectMode && photographerTypes.length === 0 && (
        <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <p className="text-sm text-warning font-medium">
            هذا المصور لم يحدد أنواع التصوير المتاحة لديه بعد.
          </p>
        </div>
      )}
      {/* متاح فقط — مخفي من UI */}
      {availableTypes.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>
            {isDirectMode && photographerTypes.length > 0
              ? 'لا يوجد تقاطع بين تخصصات هذا المصور وأنواع التصوير المسموحة لفئتك. جرّب مصوراً آخر أو راجع فئة التصوير.'
              : 'لا توجد أنواع تصوير متاحة. يرجى العودة واختيار فئة التصوير أولاً.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {availableTypes?.map((type) => {
            const isComingSoon = COMING_SOON_TYPES.has(type?.value);
            return (
              <div key={type?.value} className="relative">
                <button
                  onClick={() => !isComingSoon && handleTypeChange(type?.value, type?.label)}
                  disabled={
                    isComingSoon ||
                    (isDirectMode && photographerTypes.length > 0 && !photographerTypes.includes(type.value))
                  }
                  className={`w-full p-5 rounded-xl border-2 text-right transition-all ${
                    isComingSoon
                      ? 'border-border bg-background opacity-50 cursor-not-allowed'
                      : formData?.photographyType === type?.value
                      ? 'border-primary bg-primary/10 shadow-sm'
                      : isDirectMode && photographerTypes.length > 0 && !photographerTypes.includes(type.value)
                      ? 'border-border bg-background opacity-50 cursor-not-allowed'
                      : 'border-border bg-background hover:border-primary hover:bg-primary/5'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-base font-semibold text-foreground mb-1">
                        {type?.label}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {type?.description}
                      </div>
                      <div className="text-xs text-primary font-medium mt-2">
                        {type?.priceRange}
                      </div>
                    </div>
                    {formData?.photographyType === type?.value && (
                      <Icon name="CheckCircle2" size={20} color="var(--color-primary)" className="flex-shrink-0" />
                    )}
                  </div>
                </button>
                {isComingSoon && (
                  <span className="absolute top-3 left-3 text-[10px] font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded-full">
                    قريباً
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </QuestionCard>
  );
};

export default PhotographyTypeStep;